// Two checks on a customer-facing reply (2026-10-23, prod contacts 8528021
// and 8524093; docs/agent-draft-shadow.md, "Templates are the model" and
// "Never claim a staged action as done"; docs/pipeline-behaviors.md,
// "Template slot check").
//
// 1. Template slots. A reply that follows a template keeps each of its
//    content pieces, filled with real data. templateSlots() lists a
//    template's slots: its INSERT... tokens (a token ending in LINK is a link
//    slot; any other is a value slot, named by its line's label), and its
//    "Reference ID" line, and its line about copying and pasting a link into
//    the browser (2026-10-26). missingTemplatePieces() checks a reply against
//    them: a link slot needs a URL (one of the known links when there are
//    any), a Reference ID needs the order number, the copy-and-paste line
//    needs its words, a labeled value slot needs its label. A token-only
//    value line cannot be checked and is skipped. A slot exists only when the
//    template has its line: an eBay "Label Where is it" has no Reference ID
//    line, so no Reference ID is ever asked of it. The caller picks the kinds
//    that apply (a Website email leaves out the copy-and-paste line, its link
//    is a real link; eBay and Walmart keep it, their link is plain text).
//
//    One step wording for any content piece left out (2026-10-26, Jenn):
//    missingPieceStep(), for these template slots and for the named sentence
//    pieces of a Sales Hold message (agent-draft-checks.js's
//    missingSalesHoldPieces()). Since 2026-10-29 only a data piece
//    (isDataSlot(): a link, the Reference ID, a number, an amount, the refund
//    date, the photos request) is a What's next line and lowers the agent's
//    confidence to Medium; a template sentence left out is in the trace only.
//
// 2. Claims of something done. findDoneClaims() finds a reply's statements
//    that something already happened: resent, refunded, cancelled or created
//    ("we've resent", "your order has been cancelled"). A caller compares
//    them with the facts (unbackedDoneClaims()): a staged action is not done.
//    A sentence with a condition (if, once, when, after, as soon as, until)
//    or a question is never a claim. Since 2026-10-26 a claim that a return
//    label was sent ("we've sent your return label", "your return label has
//    been sent") is its own kind, 'sent'; a staged step of the same kind that
//    runs before the send backs it (agent-reply-checks.js's stagedKinds).
//
// Pure: no calls.
import { findLinks } from './ai.js'
import { htmlToPlainText, looksLikeHtml } from './text-format.js'
import { RETURN_LABEL_SRC } from './promised-actions.js'
import { RMA_REFERENCE_TOKEN } from './rmartn-cust-pays.js'

const TOKEN_RE = /\bINSERT[A-Z0-9_]+\b/g
const REFERENCE_ID_RE = /\b(?:Your\s+)?Reference\s+ID\b/i
const COPY_PASTE_RE = /\bcopy\s+(?:and|&)\s+paste\b/i
const TRACKING_LINK_LINE_RE = /^\s*[-*•]?\s*tracking\s+link\b/i

const plain = (text) => {
  const s = String(text ?? '')
  return looksLikeHtml(s) ? htmlToPlainText(s) : s
}
const norm = (s) => String(s ?? '').replace(/[‘’]/g, "'").replace(/\s+/g, ' ').trim().toLowerCase()

// The slots of a template's content (HTML or plain text):
// [{ kind: 'link' | 'value' | 'reference_id' | 'copy_paste', token, label, name }].
export function templateSlots(content) {
  const text = plain(content)
  const slots = []
  const seen = new Set()
  for (const line of text.split('\n')) {
    const tokens = line.match(TOKEN_RE) ?? []
    for (const token of tokens) {
      if (seen.has(token)) continue
      seen.add(token)
      const label = line.replace(TOKEN_RE, ' ').replace(/^[\s•*\-–—]+/, '').replace(/[\s:#*\-–—]+$/, '').replace(/\s+/g, ' ').trim()
      if (/LINK$/.test(token)) slots.push({ kind: 'link', token, label, name: token === 'INSERTLABELLINK' ? 'the label link' : 'the link' })
      else slots.push({ kind: 'value', token, label, name: label ? `the "${label}" line` : token })
    }
    if (REFERENCE_ID_RE.test(line) && !seen.has('REFERENCE_ID')) {
      seen.add('REFERENCE_ID')
      slots.push({ kind: 'reference_id', token: null, label: 'Reference ID', name: 'the Reference ID' })
    }
    if (COPY_PASTE_RE.test(line) && !seen.has('COPY_PASTE')) {
      seen.add('COPY_PASTE')
      slots.push({ kind: 'copy_paste', token: null, label: 'copy and paste', name: 'the line about copying and pasting the link into the browser' })
    }
    // The label templates' sentence pieces (2026-10-27, Jenn): the mobile /
    // QR code option, the drop-off within the template's window and the
    // refund timeline. Checked only when a caller asks for their kinds
    // (agent-reply-checks.js's slotKindsFor()); the default kinds leave them
    // out, so the pipeline's own template check is unchanged.
    for (const p of LABEL_SENTENCE_PIECES) {
      if (p.template.test(line) && !seen.has(p.kind)) {
        seen.add(p.kind)
        slots.push({ kind: p.kind, token: null, label: null, name: p.name })
      }
    }
  }
  return slots
}

// The label templates' sentence pieces: a template line that has the piece
// (template) and a reply that keeps it (reply).
const LABEL_SENTENCE_PIECES = Object.freeze([
  { kind: 'qr_code', name: 'the mobile/QR code option', template: /\b(?:QR|mobile)\s+code\b|\bQR\b/i, reply: /\b(?:QR|mobile)\b/i },
  { kind: 'drop_off', name: 'the drop-off step', template: /\bdrop(?:\s|-)?(?:it\s+)?off\b/i, reply: /\bdrop(?:\s|-)?(?:it\s+|the\s+\w+\s+|your\s+\w+\s+)?off\b/i },
  { kind: 'refund_timeline', name: 'the refund timeline', template: /\brefund\s+timeline\b|\brefund\b[^\n]{0,60}\bbusiness\s+days\b/i, reply: /\brefund\b[^\n]{0,80}\b(?:business\s+)?days\b|\brefund\s+timeline\b/i },
])
export const LABEL_PIECE_KINDS = Object.freeze(LABEL_SENTENCE_PIECES.map((p) => p.kind))

// The pieces a reply leaves out: [name]. links: the links the slot must be
// filled with when known (a recovered label's); erpOrderId: the Reference
// ID's value; exemptTokens: link or value tokens filled later (a label
// created at Process); skipReferenceId: the Reference ID line is removed by
// rule. kinds: the slot kinds to check (default all). pendingTokens
// (2026-10-26): link tokens a staged step fills at Process (a label recovered
// then, @cxhub/shared's label-link-slot.js): the slot is filled when the
// reply keeps its token.
// referenceIds (2026-10-28): other numbers that count as the Reference ID
// (the order's RMAs: an existing RMA's reply carries the RMA there).
export function missingTemplatePieces(opts = {}) {
  return missingTemplateSlots(opts).map((s) => s.name)
}

// The slots a reply leaves out, as slots ({ kind, token, label, name }), one
// per name; the same arguments as missingTemplatePieces().
export function missingTemplateSlots({ slots = [], reply = '', links = [], erpOrderId = null, referenceIds = [], exemptTokens = [], pendingTokens = [], skipReferenceId = false, kinds = ['link', 'value', 'reference_id', 'copy_paste'] } = {}) {
  const raw = String(reply ?? '')
  const text = norm(plain(raw))
  const replyLinks = findLinks(raw)
  const missing = []
  for (const s of slots) {
    if (!kinds.includes(s.kind)) continue
    if (s.token && exemptTokens.includes(s.token)) continue
    let ok = true
    if (s.kind === 'link') {
      const known = (links ?? []).filter(Boolean)
      ok = known.length ? known.some((u) => raw.includes(u)) : replyLinks.length > 0
      if (!ok && s.token && pendingTokens.includes(s.token) && raw.includes(s.token)) ok = true
    } else if (s.kind === 'reference_id') {
      if (skipReferenceId) continue
      // The RMA number's token (2026-10-27, rmartn-cust-pays.js): an "RMARTN
      // Cust Pays" reply's Reference ID, filled when the return is processed.
      ok = [erpOrderId, ...(referenceIds ?? [])].some((id) => Boolean(id) && text.includes(norm(id))) || raw.includes(RMA_REFERENCE_TOKEN)
    } else if (s.kind === 'copy_paste') {
      // The tracking block's own label ("Tracking link (copy and paste into
      // your browser):", agent-draft.js's TRACKING_LINK_LABEL, on eBay and
      // since 2026-10-27 on Walmart) is not the template's line.
      ok = COPY_PASTE_RE.test(norm(plain(raw).split('\n').filter((l) => !TRACKING_LINK_LINE_RE.test(l)).join('\n')))
    } else if (LABEL_PIECE_KINDS.includes(s.kind)) {
      ok = LABEL_SENTENCE_PIECES.find((p) => p.kind === s.kind).reply.test(text)
    } else {
      if (!s.label || s.label.length < 3) continue
      ok = text.includes(norm(s.label))
    }
    if (!ok && !missing.some((m) => m.name === s.name)) missing.push(s)
  }
  return missing
}

// Data pieces (2026-10-29, Jenn; docs/agent-draft-shadow.md "What's next
// lists only what the CSR wouldn't otherwise see"): what the CSR may not
// have at hand, so a reply that leaves it out keeps its What's next line and
// lowers the confidence. A link (a label, tracking or eBay return link), the
// Reference ID, and a value slot for the RMA / order / PO / return document
// number, a refund or label amount, a date (any INSERT...DATE token: the
// refund date, and since Jenn's answer of 2026-10-29 a plain INSERTDATE such
// as "arrived on INSERTDATE") or a tracking number; a line that asks for
// photos (a required form). Every other piece is template text (the
// copy-and-paste line, the QR / drop-off / refund timeline sentences, a
// sentence with a payment type or posting days in it): the CSR reads
// the reply before sending, so it stays in the trace and the draft's details
// only.
const DATA_TOKEN_RE = /^INSERT(?:RMA|REFERENCE|ORDERNUMBER|PONUMBER|RETURNDOCUMENT|RETURNERPORDERID|TRACKING|EBAYITEMNUMBER|\w*DATE|\w*AMOUNT|LABELDEDUCTION)\w*$/
const PHOTOS_RE = /\b(?:photos?|pictures?|images?)\b/i
export function isDataSlot(slot) {
  if (!slot) return false
  if (slot.kind === 'link' || slot.kind === 'reference_id') return true
  if (slot.kind === 'value') return DATA_TOKEN_RE.test(String(slot.token ?? '')) || PHOTOS_RE.test(String(slot.label ?? ''))
  return false
}

// The What's next item for a piece a reply leaves out: one wording for every
// source (a template slot, a named Sales Hold piece; 2026-10-26). title: the
// template or model message the piece belongs to, when it has one.
// Since 2026-10-30 (Jenn) it is no longer a What's next line of the agent
// draft: a data piece is added back to the reply in code
// (agent-reply-checks.js's addBackDataPieces()) and What's next only asks to
// fill a placeholder (fillPlaceholderStep(), fillPieceStep()). The
// pipeline's own template check still uses this wording in its review
// reason.
export function missingPieceStep(piece, title) {
  return `The reply leaves out ${piece}${title ? ` from "${title}"` : ''}.`
}

// The What's next line for a placeholder left in the reply (2026-10-30):
// "Fill in INSERTREFUNDDATE (Next Steps: Your refund is expected to appear
// by)." label: the template line's label, when it has one.
export function fillPlaceholderStep(token, label = null) {
  const l = String(label ?? '').trim()
  return `Fill in ${token}${l ? ` (${l})` : ''}.`
}

// The What's next line for a data piece code could not add (2026-10-30):
// "Fill in the label link in the reply."
export function fillPieceStep(piece) {
  return `Fill in ${piece} in the reply.`
}

// --- Claims of something done ------------------------------------------------

export const DONE_CLAIM_KINDS = Object.freeze(['resent', 'sent', 'refunded', 'cancelled', 'created'])

const PERF = String.raw`\b(?:we|i)(?:'ve|\s+have)\s+(?:also\s+|now\s+|already\s+|just\s+|gone\s+ahead\s+and\s+)?`
const PASSIVE = String.raw`\b(?:has|have)\s+(?:also\s+|now\s+|already\s+|just\s+)?been\s+`
// A label (2026-10-26): a return label as promised-actions.js defines it
// (RETURN_LABEL_SRC, the one definition situation-brief.js's reply check
// also uses), or "label" / "shipping label" alone, which in a claim of
// sending can only be the label we send the customer.
const LABEL_WORD = String.raw`(?:${RETURN_LABEL_SRC}|(?:shipping\s+)?label)`
const DONE_PATTERNS = Object.freeze([
  ['resent', new RegExp(String.raw`${PERF}re-?sent\b|${PASSIVE}re-?sent\b`, 'i')],
  // A return label sent (2026-10-26): "we've sent (you) your return label",
  // "your prepaid return label has been sent".
  ['sent', new RegExp(String.raw`${PERF}sent\b(?:\s+[\w'-]+){0,4}?\s+${LABEL_WORD}\b|\b${LABEL_WORD}s?\s+${PASSIVE}sent\b`, 'i')],
  ['refunded', new RegExp(String.raw`${PERF}(?:refunded|issued\s+(?:a|your|the)\s+(?:full\s+|partial\s+)?refund|processed\s+(?:a|your|the)\s+(?:full\s+|partial\s+)?refund)\b|${PASSIVE}refunded\b|\brefund\s+${PASSIVE.slice(2)}(?:issued|processed)\b`, 'i')],
  ['cancelled', new RegExp(String.raw`${PERF}cancell?ed\b|${PASSIVE}cancell?ed\b`, 'i')],
  ['created', new RegExp(String.raw`${PERF}created\b|${PASSIVE}created\b`, 'i')],
])
const CONDITION_RE = /\b(?:if|once|when|whenever|after|as soon as|until|unless)\b/i

// [{ kind, sentence }] for each statement that something already happened.
export function findDoneClaims(reply) {
  const text = plain(reply).replace(/[‘’]/g, "'")
  const out = []
  for (const sentence of text.split(/(?<=[.!?])\s+|\n+/)) {
    const s = sentence.trim()
    if (!s || /\?\s*$/.test(s) || CONDITION_RE.test(s)) continue
    for (const [kind, re] of DONE_PATTERNS) {
      if (re.test(s) && !out.some((c) => c.kind === kind)) out.push({ kind, sentence: s.slice(0, 160) })
    }
  }
  return out
}

// The claims no fact backs. facts: { refunded, cancelled, created, resent,
// sent } booleans (what the order's documents and facts show already
// happened, or, since 2026-10-26, a staged step of that kind that runs
// before the reply is sent).
export function unbackedDoneClaims(reply, facts = {}) {
  return findDoneClaims(reply).filter((c) => facts?.[c.kind] !== true)
}

// The What's next item for an unbacked claim.
export function doneClaimStep(claim) {
  return `Rewrite the reply: it says something was already ${claim.kind} ("${claim.sentence}"), but nothing on the order shows that happened and no staged step does it. Describe it as something we're doing.`
}
